import React from 'react';
import {
    Facebook,
    Instagram,
    Linkedin,
    Mail,
    Phone,
    MapPin,
    ArrowRight
} from 'lucide-react';
import USE_CASES from './useCases.json';
import BLOG_POSTS from './blogPosts.json';

const Footer = () => {
    return (
        <footer className="bg-ink text-paper pt-20 pb-10 overflow-hidden relative">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-10 lg:gap-8 mb-16">
                    {/* Brand Section */}
                    <div className="flex flex-col gap-6">
                        <div className="flex items-center gap-3">
                            <img
                                src="/new_logo.png"
                                alt="Vetobase AI"
                                className="w-11 h-11 rounded-xl object-contain bg-white/5 p-0.5"
                            />
                            <div className="flex flex-col leading-tight">
                                <span className="text-xl font-bold text-paper tracking-head">
                                    Vetobase <span className="font-serif-italic">AI</span>
                                </span>
                                <span className="text-[10px] font-semibold text-paper/60 uppercase tracking-label">(SMC-Private) Limited</span>
                            </div>
                        </div>
                        <p className="text-paper/60 leading-relaxed text-sm">
                            Vetobase AI helps businesses automate customer support, sync data across platforms, and scale operations with intelligent AI agents.
                        </p>
                        <div className="flex gap-4">
                            {[
                                { icon: <Facebook className="w-5 h-5" />, href: "#" },
                                { icon: <Instagram className="w-5 h-5" />, href: "#" },
                                { icon: <Linkedin className="w-5 h-5" />, href: "#" }
                            ].map((social, i) => (
                                <a
                                    key={i}
                                    href={social.href}
                                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-paper/60 hover:text-paper hover:border-paper hover:bg-white/10 transition-all"
                                >
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-paper font-bold mb-6 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-paper rounded-full"></span>
                            Platform
                        </h4>
                        <ul className="flex flex-col gap-4">
                            {[
                                { label: 'Product tour', href: '#product' },
                                { label: 'Solutions', href: '#solutions' },
                                { label: 'Pricing', href: '#pricing' },
                                { label: 'Watch the demo', href: '/watch/', external: true },
                                { label: 'All use cases', href: '/use-cases/' },
                                { label: 'Blog', href: '/blog/' },
                                { label: 'Partners', href: '#partners' },
                                { label: 'Contact', href: '#contact' },
                            ].map(link => (
                                <li key={link.label}>
                                    <a href={link.href} {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="text-paper/60 hover:text-paper text-sm font-medium transition-colors flex items-center gap-2 group">
                                        <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Use cases */}
                    <div>
                        <h4 className="text-paper font-bold mb-6 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-paper rounded-full"></span>
                            Use cases
                        </h4>
                        <ul className="flex flex-col gap-3">
                            {USE_CASES.slice(0, 7).map((u) => (
                                <li key={u.slug}><a href={'/use-cases/' + u.slug + '/'} className="text-paper/60 hover:text-paper text-sm font-medium transition-colors">{u.label}</a></li>
                            ))}
                        </ul>
                    </div>

                    {/* More use cases + company */}
                    <div>
                        <h4 className="text-paper font-bold mb-6 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-paper rounded-full"></span>
                            Features
                        </h4>
                        <ul className="flex flex-col gap-3">
                            {USE_CASES.slice(7).map((u) => (
                                <li key={u.slug}><a href={'/use-cases/' + u.slug + '/'} className="text-paper/60 hover:text-paper text-sm font-medium transition-colors">{u.label}</a></li>
                            ))}
                        </ul>
                        <div className="mt-6 text-sm text-paper/60">
                            <div className="font-semibold text-paper">Vetobase (SMC-Private) Limited</div>
                            <div className="mt-1 leading-relaxed">1032 Ravi Block, Allama Iqbal Town, Lahore 54000, Punjab, Pakistan</div>
                            <div className="mt-1">SECP CUIN 0333406</div>
                        </div>
                    </div>

                    {/* Blog — latest articles */}
                    <div>
                        <h4 className="text-paper font-bold mb-6 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-paper rounded-full"></span>
                            Blog
                        </h4>
                        <ul className="flex flex-col gap-3">
                            {BLOG_POSTS.slice(0, 5).map((p) => (
                                <li key={p.slug}><a href={'/blog/' + p.slug + '/'} className="text-paper/60 hover:text-paper text-sm font-medium leading-snug transition-colors">{p.title}</a></li>
                            ))}
                            <li><a href="/blog/" className="text-paper text-sm font-bold underline underline-offset-4">All articles</a></li>
                        </ul>
                    </div>

                    {/* Get in Touch */}
                    <div className="flex flex-col gap-5">
                        <h4 className="text-paper font-bold flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-paper rounded-full"></span>
                            Get in Touch
                        </h4>
                        <a
                            href="mailto:support@vetobase.com"
                            className="flex items-center gap-3 text-paper/60 hover:text-paper text-sm font-medium transition-colors"
                        >
                            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/15 flex items-center justify-center">
                                <Mail className="w-4 h-4" />
                            </div>
                            <span className="break-all">support@vetobase.com</span>
                        </a>
                        <a
                            href="tel:+923124012324"
                            className="flex items-center gap-3 text-paper/60 hover:text-paper text-sm font-medium transition-colors"
                        >
                            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/15 flex items-center justify-center">
                                <Phone className="w-4 h-4" />
                            </div>
                            <span>+92 312 4012324 (phone &amp; WhatsApp)</span>
                        </a>
                        <div className="flex items-start gap-3 text-paper/60 text-sm font-medium">
                            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/15 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-4 h-4" />
                            </div>
                            <span className="leading-relaxed">1032 Ravi Block, Allama Iqbal Town, Lahore 54000, Pakistan</span>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-10 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
                    <p className="text-paper/50 text-xs text-center md:text-left">
                        &copy; {new Date().getFullYear()} Vetobase (SMC-Private) Limited. All rights reserved.
                    </p>
                    <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                        <a href="/privacy.html" className="text-paper/50 hover:text-paper text-xs transition-colors">Privacy Policy</a>
                        <a href="/terms.html" className="text-paper/50 hover:text-paper text-xs transition-colors">Terms &amp; Conditions</a>
                        <a href="/refund.html" className="text-paper/50 hover:text-paper text-xs transition-colors">Cancellation &amp; Refund Policy</a>
                        <a href="/complaints.html" className="text-paper/50 hover:text-paper text-xs transition-colors">Complaints</a>
                        <a href="/ownership.html" className="text-paper/50 hover:text-paper text-xs transition-colors">Ownership Statement</a>
                        <a href="https://omarfaiz.vetobase.com/" target="_blank" rel="noopener noreferrer" className="text-paper/50 hover:text-paper text-xs transition-colors">Founder</a>
                        <a href="/sitemap.xml" className="text-paper/50 hover:text-paper text-xs transition-colors">Sitemap</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
